import React, { useState, useEffect } from 'react';
import api, { apiWithDebug } from '../api';

const Uploads = () => {
    const [chatText, setChatText] = useState('');
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [topic1, setTopic1] = useState('');
    const [topic2, setTopic2] = useState('');
    const [topics, setTopics] = useState([]);
    const [model, setModel] = useState('');
    const [models, setModels] = useState([]);
    const [files, setFiles] = useState([]);
    const [showModelList, setShowModelList] = useState(false);
    const [allModels, setAllModels] = useState([]);
    const [fileUploadMessage, setFileUploadMessage] = useState('');
    // new: granular status while batch uploading
    const [uploadStatus, setUploadStatus] = useState('');
    // Track individual file statuses
    const [fileStatuses, setFileStatuses] = useState({});
    const [importBatch, setImportBatch] = useState(null);
    const [importItems, setImportItems] = useState([]);
    const [importDecisions, setImportDecisions] = useState({});
    const [refinementRunning, setRefinementRunning] = useState(false);
    const [committingItemId, setCommittingItemId] = useState(null);

    useEffect(() => {
        api.get('/topics').then(res => setTopics(res.data));
    }, []);

    useEffect(() => {
        api.get('/uploads/json/batches?limit=10')
            .then(async response => {
                const batches = response.data?.batches || [];
                const resumable = batches.find(batch =>
                    ['staged', 'processing', 'ready_for_review', 'completed_with_errors'].includes(batch.status)
                );
                if (resumable) {
                    const data = await loadImportBatch(resumable.id, true);
                    if (data.refinementRunning) monitorImportBatch(resumable.id);
                }
            })
            .catch(error => {
                // Deployments without the staging migration keep the legacy
                // upload screen usable until the migration is applied.
                console.debug('No resumable JSON import batch available:', error.message);
            });
        // loadImportBatch is intentionally stable for the lifetime of this
        // screen; this boot-time recovery check runs once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load model lists from backend API (CSV is now parsed server-side)
    useEffect(() => {
        // Full list (for modal)
        api.get('/models')
            .then(res => setAllModels(res.data))
            .catch(err => console.error('Error fetching full model list:', err));

        // Accepted list (for dropdown)
        api.get('/models/accepted')
            .then(res => setModels(res.data))
            .catch(err => console.error('Error fetching accepted model list:', err));
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const handleTextSubmit = async () => {
        if (!title.trim() || !chatText.trim() || !date || !topic1 || !model) {
            alert("All fields required (title, date, at least one topic, model, text). Second topic is optional.");
            return;
        }
        if (topic2 && topic1 === topic2) {
            alert("Primary and secondary topics must be different.");
            return;
        }
        const topicArr = topic2 ? [parseInt(topic1), parseInt(topic2)] : [parseInt(topic1)];

        setIsSubmitting(true);
        setSubmitMessage('Processing... please wait.');

        try {
            await api.post('/uploads/text', {
                chat_text: chatText,
                chat_title: title,
                chat_date: date,
                topic: topicArr,
                model: model
            });

            setSubmitMessage('✅ Chat uploaded successfully!');
            setChatText('');
            setTitle('');
            setDate('');
            setTopic1('');
            setTopic2('');
            setModel('');
        } catch (err) {
            console.error('Error uploading chat:', err);
            setSubmitMessage('❌ Failed to save chat.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ------------------------------------------------------------------
    // Sequential file processor with richer status messages
    // ------------------------------------------------------------------
    // --- Sequential file processor -------------------------------------------------
    const processFileSequential = async (file, isJson) => {
        let profileMsg = ''; // Initialize profileMsg here
        // mark processing
        setFileStatuses(prev => ({
            ...prev,
            [file.name]: { status: 'processing', message: 'Uploading …' }
        }));
        // Helper to update status message with minimal delay so user
        // perceives progress through distinct phases.
        const step = async (msg) => {
            setFileStatuses(prev => ({
                ...prev,
                [file.name]: { status: 'processing', message: msg }
            }));
            // short pause purely for UX – does not block overall flow
            await new Promise(r => setTimeout(r, 200));
        };


        try {
            const fd = new FormData();
            fd.append('files', file);
            const endpoint = isJson ? '/uploads/json/json-batch' : '/uploads/upload';
            const res = await apiWithDebug.post(endpoint, fd);
            await step('Analyzing content …');
            await step('Classifying topics …');
            await step('Generating summary …');


            // Determine success using the legacy upload response or the JSON
            // conversation-level batch response.
            let success = false;
            let msg = '';
            let imported = 0;
            let duplicates = 0;
            let failed = 0;
            if (res.data?.processedFiles?.length) {
                success = true;
                const f = res.data.processedFiles[0];
                imported = 1;
                msg = f.autoTopic ? `Topics: ${f.topics?.join(', ')}` : 'Success';
                if (f.profileSummary) {
                    profileMsg = f.profileSummary;
                }
            } else if (res.data?.results?.successfulFiles?.length) {
                success = true;
                const f = res.data.results.successfulFiles[0];
                imported = res.data.results.imported ??
                    res.data.results.successfulFiles.filter(item => !item.duplicate).length;
                duplicates = res.data.results.duplicates ??
                    res.data.results.successfulFiles.filter(item => item.duplicate).length;
                failed = res.data.results.failed ?? res.data.results.failedFiles?.length ?? 0;
                const parts = [];
                if (imported) parts.push(`${imported} imported`);
                if (duplicates) parts.push(`${duplicates} duplicate${duplicates === 1 ? '' : 's'} skipped`);
                if (failed) parts.push(`${failed} failed`);
                msg = parts.join(' · ') || 'No conversations imported';
                if (f.profileSummary) {
                    profileMsg = f.profileSummary;
                }
            } else if (res.data?.results) {
                failed = res.data.results.failed ?? res.data.results.failedFiles?.length ?? 1;
                msg = `${failed} conversation${failed === 1 ? '' : 's'} failed`;
            }

            if (success) {
                setFileStatuses(prev => ({
                    ...prev,
                    [file.name]: {
                        status: failed ? 'warning' : 'success',
                        message: msg,
                        profile: profileMsg
                    }
                }));
                return { ok: failed === 0, note: msg, imported, duplicates, failed };
            } else {
                const errorText =
                    res.data?.results?.failedFiles?.[0]?.error ||
                    (res.data?.invalidFiles && res.data.invalidFiles[0]?.error) ||
                    msg ||
                    'Unknown error';
                setFileStatuses(prev => ({
                    ...prev,
                    [file.name]: { status: 'error', message: errorText }
                }));
                return { ok: false, note: errorText, imported: 0, duplicates: 0, failed: failed || 1 };

            }
        } catch (err) {
            console.error('Upload error:', err);
            const errorMsg = err.response?.data?.error || err.message || 'Request failed';
            setFileStatuses(prev => ({
                ...prev,
                [file.name]: { status: 'error', message: errorMsg }
            }));
            return { ok: false, note: errorMsg, imported: 0, duplicates: 0, failed: 1 };
        }
    };

    const loadImportBatch = async (batchId, includeItems = false) => {
        const suffix = includeItems ? '?includeItems=true&limit=500' : '';
        const response = await api.get(`/uploads/json/batches/${batchId}${suffix}`);
        setImportBatch(response.data.batch);
        setRefinementRunning(Boolean(response.data.refinementRunning));
        if (response.data.items) setImportItems(response.data.items);
        return response.data;
    };

    const monitorImportBatch = async (batchId) => {
        while (true) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            const data = await loadImportBatch(batchId);
            const batch = data.batch;
            setUploadStatus(
                `Refining archive: ${batch.ready_items + batch.duplicate_items + batch.failed_items}` +
                ` / ${batch.total_items} examined · ${batch.pending_items} remaining`
            );
            if (!data.refinementRunning) {
                await loadImportBatch(batchId, true);
                setUploadStatus('');
                if (batch.pending_items > 0) {
                    setFileUploadMessage(
                        `Archive is safe. Refinement paused with ${batch.pending_items} conversation(s) remaining; use Resume refinement.`
                    );
                } else {
                    setFileUploadMessage(
                        `${batch.ready_items} conversation(s) ready for review · ` +
                        `${batch.duplicate_items} duplicate(s) · ${batch.failed_items} failed.`
                    );
                }
                return;
            }
        }
    };

    const startRefinement = async (batchId) => {
        setUploadStatus('Starting restart-safe archive refinement…');
        await api.post(`/uploads/json/batches/${batchId}/refine`);
        setRefinementRunning(true);
        await monitorImportBatch(batchId);
    };

    const stageJsonArchive = async (jsonFiles) => {
        const statuses = {};
        jsonFiles.forEach(file => {
            statuses[file.name] = { status: 'processing', message: 'Preserving archive…' };
        });
        setFileStatuses(statuses);
        setImportBatch(null);
        setImportItems([]);
        setImportDecisions({});
        setUploadStatus('Preserving conversations before AI processing…');

        try {
            const formData = new FormData();
            jsonFiles.forEach(file => formData.append('files', file));
            const response = await apiWithDebug.post('/uploads/json/stage', formData);
            const batch = response.data.batch;
            setImportBatch(batch);
            setFiles([]);
            const stagedStatuses = {};
            jsonFiles.forEach(file => {
                stagedStatuses[file.name] = {
                    status: 'success',
                    message: `${batch.total_items} conversation(s) safely staged in batch ${batch.id}`
                };
            });
            setFileStatuses(stagedStatuses);
            setFileUploadMessage(
                `✅ Archive batch ${batch.id} is preserved. You can safely resume it after a restart.`
            );
            await startRefinement(batch.id);
        } catch (error) {
            const message = error.response?.data?.error || error.message || 'Archive staging failed';
            const failedStatuses = {};
            jsonFiles.forEach(file => {
                failedStatuses[file.name] = { status: 'error', message };
            });
            setFileStatuses(failedStatuses);
            setFileUploadMessage(`❌ ${message}`);
            setUploadStatus('');
        }
    };

    const retryFailedImportItems = async () => {
        if (!importBatch) return;
        await api.post(`/uploads/json/batches/${importBatch.id}/retry-failed`);
        await startRefinement(importBatch.id);
    };

    const selectedTopics = (item) =>
        importDecisions[item.id]?.topics || item.analysis?.topics || [];

    const selectedProfileSuggestions = (item) =>
        importDecisions[item.id]?.profileSuggestionIndexes || [];

    const toggleImportTopic = (item, topicId) => {
        const current = selectedTopics(item);
        const next = current.includes(topicId)
            ? current.filter(id => id !== topicId)
            : [...current, topicId];
        setImportDecisions(previous => ({
            ...previous,
            [item.id]: { ...previous[item.id], topics: next }
        }));
    };

    const toggleProfileSuggestion = (item, suggestionIndex) => {
        const current = selectedProfileSuggestions(item);
        const next = current.includes(suggestionIndex)
            ? current.filter(index => index !== suggestionIndex)
            : [...current, suggestionIndex];
        setImportDecisions(previous => ({
            ...previous,
            [item.id]: { ...previous[item.id], profileSuggestionIndexes: next }
        }));
    };

    const approveImportItem = async (item) => {
        const topicsForItem = selectedTopics(item);
        if (!topicsForItem.length) {
            alert('Select at least one topic before approving this conversation.');
            return;
        }
        setCommittingItemId(item.id);
        try {
            await api.post(
                `/uploads/json/batches/${importBatch.id}/items/${item.id}/commit`,
                {
                    topics: topicsForItem,
                    profileSuggestionIndexes: selectedProfileSuggestions(item)
                }
            );
            await loadImportBatch(importBatch.id, true);
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to commit conversation.');
        } finally {
            setCommittingItemId(null);
        }
    };

    // Handles both legacy TXT/PDF uploads and new JSON batch uploads
    const handleFileBatchUpload = () => {
        // Reset previous state
        setUploadStatus('Uploading files…');
        setFileUploadMessage('');
        const jsonFiles = [];
        const otherFiles = [];

        // Split files by extension so we don't mix formats in one call
        for (const file of files) {
            const isJson = file.name.toLowerCase().endsWith('.json');
            (isJson ? jsonFiles : otherFiles).push(file);
        }

        if (jsonFiles.length && otherFiles.length) {
            alert('Please upload JSON files separately from TXT/PDF files.');
            setUploadStatus('');
            return;
        }

        if (jsonFiles.length) {
            stageJsonArchive(jsonFiles);
            return;
        }

        // Initialize file statuses
        const initialStatuses = {};
        (jsonFiles.length ? jsonFiles : otherFiles).forEach(file => {
            initialStatuses[file.name] = { status: 'queued', message: 'Waiting...' };
        });
        setFileStatuses(initialStatuses);

        // Sequential processing queue
        const queue = otherFiles;
        const isJsonBatch = false;
        let successCount = 0;
        let failCount = 0;
        let importedCount = 0;
        let duplicateCount = 0;
        let conversationFailCount = 0;

        const runQueue = async () => {
            for (const f of queue) {
                const result = await processFileSequential(f, isJsonBatch);
                if (result.ok) successCount++;
                else failCount++;
                importedCount += result.imported || 0;
                duplicateCount += result.duplicates || 0;
                conversationFailCount += result.failed || 0;
            }

            // Build summary
            let summary = '';
            if (isJsonBatch) {
                if (importedCount) summary += `✅ ${importedCount} conversation(s) imported.\n`;
                if (duplicateCount) summary += `↪ ${duplicateCount} duplicate conversation(s) skipped.\n`;
                if (conversationFailCount) summary += `⚠ ${conversationFailCount} conversation(s) need attention.\n`;
            } else {
                if (successCount) summary += `✅ ${successCount} file(s) uploaded successfully.\n`;
                if (failCount) summary += `❌ ${failCount} file(s) failed.\n`;
            }
            setFileUploadMessage(summary || 'No files processed.');
            setUploadStatus('');
            setFiles([]);
        };

        runQueue();
    };

    return (
        <div className="terminal min-h-screen flex flex-col items-center justify-start px-4 py-6 font-mono text-green-300">
            <div className="w-full max-w-4xl">
                <h2 className="text-green-400 text-xl mb-6 text-center">INSERT (Chat History Event)</h2>

                {/* Model List Button */}
                <div className="flex gap-4 justify-center mb-4">
                    <button className="btn-secondary" onClick={() => setShowModelList(true)}>
                        Model List (Full CSV)
                    </button>
                </div>

                {/* Model List Modal */}
                {showModelList && (
                    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                        <div className="bg-green-950 border border-green-500 p-4 rounded shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
                            <button onClick={() => setShowModelList(false)} className="absolute top-2 right-2 text-green-400 z-10">✕</button>
                            <h3 className="text-green-400 text-xl mb-4">Full Model List (CSV)</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr>
                                            {allModels[0] && Object.keys(allModels[0]).map((col, idx) => (
                                                <th key={idx} className="border-b border-green-700 px-2 py-1 text-green-300 font-bold">{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allModels.map((row, i) => (
                                            <tr key={i} className="border-b border-green-800">
                                                {Object.values(row).map((val, j) => (
                                                    <td key={j} className="px-2 py-1 text-green-200">{val}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                <div className="border border-green-500 rounded p-6 mb-8 bg-black shadow-lg">
                    <h3 className="text-green-400 text-lg mb-4">Manual Mode — Direct Copy/Paste</h3>

                    <input
                        className="input-terminal mb-3"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Chat Title"
                    />

                    <textarea
                        className="input-terminal mb-3"
                        value={chatText}
                        onChange={(e) => setChatText(e.target.value)}
                        placeholder="Paste full chat log here..."
                        rows={12}
                    />

                    <div className="flex flex-col sm:flex-row gap-3 mb-3">
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="input-terminal"
                        />
                        <select
                            value={topic1}
                            onChange={e => setTopic1(e.target.value)}
                            className="input-terminal"
                        >
                            <option value="">Primary Topic</option>
                            {topics.map(t => (
                                <option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>
                            ))}
                        </select>
                        <select
                            value={topic2}
                            onChange={e => setTopic2(e.target.value)}
                            className="input-terminal"
                        >
                            <option value="">Secondary Topic (optional)</option>
                            {topics
                                .filter(t => t.topic_id !== parseInt(topic1))
                                .map(t => (
                                    <option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>
                                ))}
                        </select>
                        <select
                            value={model}
                            onChange={e => setModel(e.target.value)}
                            className="input-terminal"
                        >
                            <option value="">Select Model</option>
                            {models.map(m => (
                                <option
                                    key={m['Model Code']}
                                    value={m['Model Code']}
                                    style={{ color: m.Color || '#ffffff' }}
                                >
                                    &#x25CF;&nbsp;
                                    {m['Model Name (Version)']} ({m.Provider})
                                </option>
                            ))}
                        </select>
                    </div>

                    <button onClick={handleTextSubmit} className="btn-primary w-full mt-2" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit for Processing'}
                    </button>
                    <p className="text-xs mt-2 text-green-400">{submitMessage}</p>
                </div>

                <div className="border border-green-500 rounded p-6 bg-black shadow-lg">
                    <h3 className="text-green-400 text-lg mb-2 text-center">
                        File(s) Upload Mode
                    </h3>
                    <p className="text-xs text-green-500 mb-4 text-center leading-snug">
                        <strong>For JSON Files:</strong> Any name is fine!&nbsp;
                        <span className="whitespace-nowrap">Auto-detects&nbsp;topics&nbsp;and&nbsp;model.</span><br />
                        <code>.json</code> exports from browser extensions work automatically.
                        <br /><br />
                        <strong>For&nbsp;TXT/PDF&nbsp;Files:</strong> Must follow&nbsp;
                        <code>DDMMMYYYY-##-XXXX-Chat&nbsp;Title.txt</code><br />
                        &nbsp;&nbsp;&ndash;&nbsp;##: digits (primary 1-5, secondary 1-5 or 0)<br />
                        &nbsp;&nbsp;&ndash;&nbsp;XXXX: 4-char model code (<em>see Model List</em>)<br />
                        Example:&nbsp;<code>01JAN2024-10-OG4O-My&nbsp;Chat.txt</code>
                    </p>

                    <input
                        type="file"
                        multiple
                        accept=".txt,.pdf,.json"
                        onChange={(e) => setFiles(e.target.files)}
                        className="input-terminal mb-4"
                    />

                    {/* Display per-file status */}
                    {Object.keys(fileStatuses).length > 0 && (
                        <div className="mb-4 text-xs">
                            <p className="text-green-400 mb-1">Processing files:</p>
                            <div className="bg-black bg-opacity-60 border border-green-700 rounded p-2 max-h-32 overflow-y-auto">
                                {Object.entries(fileStatuses).map(([filename, status]) => (
                                    <div key={filename} className="mb-1 flex flex-col">
                                        <div className="flex items-center">
                                            <span className={`mr-2 ${
                                                status.status === 'queued' ? 'text-yellow-400' :
                                                status.status === 'processing' ? 'text-cyan-400 animate-pulse' :
                                                status.status === 'success' ? 'text-green-400' :
                                                status.status === 'warning' ? 'text-yellow-400' :
                                                'text-red-400'
                                            }`}>
                                                {status.status === 'queued' ? '⏳' : 
                                                status.status === 'processing' ? '🔄' :
                                                status.status === 'success' ? '✅' :
                                                status.status === 'warning' ? '⚠️' : '❌'}
                                            </span>
                                            <span className="truncate">{filename}</span>
                                            {status.message && (
                                                <span className="ml-2 text-xs opacity-70"> - {status.message}</span>
                                            )}
                                        </div>
                                        {status.profile && (
                                            <div className="ml-6 text-xs text-yellow-300">{status.profile}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Live upload status (shows while backend is processing) */}
                    {uploadStatus && (
                        <div className="mb-2 text-sm text-center text-cyan-400">
                            <span className="animate-pulse">{uploadStatus}</span>
                        </div>
                    )}

                    <button
                        onClick={handleFileBatchUpload}
                        className="btn-primary w-full"
                        disabled={Boolean(uploadStatus)}
                    >
                        Initiate Uplink & Transmit
                    </button>
                    {fileUploadMessage && (
                        <div className="mt-3 text-xs whitespace-pre-line text-green-400 bg-black bg-opacity-60 border border-green-700 rounded p-2">
                            {fileUploadMessage}
                        </div>
                    )}

                    {importBatch && (
                        <div className="mt-4 border border-cyan-700 rounded p-3 bg-black bg-opacity-70">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <h4 className="text-cyan-300">
                                    ARCHIVE BATCH {importBatch.id} · {importBatch.status}
                                </h4>
                                <div className="flex gap-2">
                                    {importBatch.pending_items > 0 && !refinementRunning && (
                                        <button
                                            className="btn-secondary text-xs"
                                            onClick={() => startRefinement(importBatch.id)}
                                        >
                                            Resume refinement
                                        </button>
                                    )}
                                    {importBatch.failed_items > 0 && !refinementRunning && (
                                        <button
                                            className="btn-secondary text-xs"
                                            onClick={retryFailedImportItems}
                                        >
                                            Retry failures
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-green-300 mb-3">
                                {importBatch.total_items} preserved · {importBatch.pending_items} pending ·{' '}
                                {importBatch.ready_items} ready · {importBatch.imported_items} committed ·{' '}
                                {importBatch.duplicate_items} duplicates · {importBatch.failed_items} failed
                            </p>
                            <p className="text-xs text-cyan-500 mb-3">
                                AI suggestions remain staged until you approve each conversation. Profile suggestions are off by default.
                            </p>

                            <div className="space-y-3 max-h-[34rem] overflow-y-auto">
                                {importItems.filter(item => item.status === 'ready_for_review').map(item => (
                                    <div key={item.id} className="border border-green-800 rounded p-3">
                                        <div className="text-green-300 mb-1">{item.chat_title}</div>
                                        <div className="text-xs text-green-600 mb-2">
                                            {item.chat_date} · {item.model} · item {item.item_order + 1}
                                        </div>
                                        <p className="text-xs text-green-400 mb-2 whitespace-pre-wrap">
                                            {item.analysis?.summary?.overview || 'No overview generated.'}
                                        </p>

                                        <fieldset className="mb-2">
                                            <legend className="text-xs text-cyan-400 mb-1">Topic membership</legend>
                                            <div className="flex flex-wrap gap-3">
                                                {topics.map(topic => {
                                                    const topicId = Number(topic.topic_id);
                                                    const score = item.analysis?.topic_scores?.[topicId] ?? 0;
                                                    return (
                                                        <label key={topicId} className="text-xs flex items-center gap-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedTopics(item).includes(topicId)}
                                                                onChange={() => toggleImportTopic(item, topicId)}
                                                            />
                                                            {topic.topic_name} ({score}%)
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </fieldset>

                                        {item.analysis?.profile_suggestions?.length > 0 && (
                                            <fieldset className="mb-3">
                                                <legend className="text-xs text-yellow-300 mb-1">
                                                    Optional profile additions
                                                </legend>
                                                <div className="space-y-1">
                                                    {item.analysis.profile_suggestions.map((suggestion, index) => (
                                                        <label key={`${item.id}-${index}`} className="text-xs flex items-start gap-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProfileSuggestions(item).includes(index)}
                                                                onChange={() => toggleProfileSuggestion(item, index)}
                                                            />
                                                            <span>{suggestion.profile_key}: {suggestion.profile_value}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </fieldset>
                                        )}

                                        <button
                                            className="btn-primary text-xs"
                                            disabled={committingItemId === item.id}
                                            onClick={() => approveImportItem(item)}
                                        >
                                            {committingItemId === item.id ? 'Committing…' : 'Approve conversation'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Uploads;
